// paymentCalculations.js — amount-paid-vs-amount-owed math for the admin
// School Payments (payment-tracking) page. Deliberately separate from
// feeCalculations.js: that file's balanceOwingForDancer/paymentStatusForDancer/
// schoolFeeSummary power the teacher-side Dancers.js/Invoices.js/PDF
// generation, and use Participation Fee's stored orgParticipationFeePaid
// boolean alongside Entry Fee's ledger-derived amounts (see
// feePaymentLedger.js) — left untouched. This module is fully amount-based,
// admin-only: amountOwed is always computed live from current roster +
// prices (never stored); amountPaid is whatever the secretary has recorded.
// Status is derived by comparing the two.

import { entryRateFor, additionalStaffFeeTotal, INCLUDED_STAFF_COUNT, ADDITIONAL_STAFF_FEE } from '../../teacher/utils/feeCalculations';

// Ledger-aggregation helpers (paid-side math) — shared with the teacher
// portal's Dancers.js, so they live in shared/utils instead of here. Re-
// exported so every existing import of this module keeps working unchanged.
export { paidForEntry, paidForEntryAndParticipant, paidForParticipant, unattributedEntryPayments, creditFor, schoolCreditBalance } from '../../shared/utils/feePaymentLedger';

export function participationFeeOwed(prices) {
  return prices['Participation Fee'] || 0;
}

export function schoolRegFeeOwed(prices) {
  return prices['School Registration Fee'] || 0;
}

// Entry fees are tracked as one running total per school (a ledger — see
// the "Entry Payments" tab), not per participant or per entry. Owed = the
// same cost-per-dancer × dancers, summed across every entry, that
// pdf.js's generateEntriesInvoicePdf already computes as its grand total.
export function schoolEntryFeeOwed(entries, prices) {
  return (entries || []).reduce(
    (sum, e) => sum + entryRateFor(e, prices) * (e.dancerIds || []).length,
    0
  );
}

// staffCount here isn't the whole school's staff count — it's this staff
// member's 0-based position within their school's roster (in existing sheet
// order), so the first 2 come back $0 and every one after owes the flat
// rate. prices is the same Prices-tab map every other owed() helper here
// takes — picks up an "Additional Staff Fee" row the same way, falling back
// to feeCalculations.js's default until that row exists on a given sheet.
export function staffMemberFeeOwed(indexWithinSchool, prices) {
  const feePerPerson = prices?.['Additional Staff Fee'] ?? ADDITIONAL_STAFF_FEE;
  return additionalStaffFeeTotal(indexWithinSchool + 1, feePerPerson) - additionalStaffFeeTotal(indexWithinSchool, feePerPerson);
}

export { INCLUDED_STAFF_COUNT, ADDITIONAL_STAFF_FEE };

// Returns { label, tone } — tone maps directly to an MUI Chip `color` prop.
// Small epsilon avoids float-rounding false positives (e.g. 24.999999...).
export function paymentStatus(amountPaid, amountOwed) {
  const diff = Math.round(((amountPaid || 0) - (amountOwed || 0)) * 100) / 100;
  if (diff < 0) return { label: `Owing $${Math.abs(diff).toFixed(2)}`, tone: 'warning' };
  if (diff > 0) return { label: `Refund owed: $${diff.toFixed(2)}`, tone: 'info' };
  return { label: 'Paid in full', tone: 'success' };
}
